// Bundles + promo codes (LSEM LMS commerce)
import { supabase } from '../integrations/supabase/client';
import { enrollInCourse } from './lms';
import { confirmPaymentClientSide } from './payments';

export interface Bundle {
  id: string;
  slug: string;
  title: string;
  description: string;
  course_ids: string[];
  price_value: number;
  compare_at_price: number;
  is_published: boolean;
}

export interface Promo {
  code: string;
  percent_off: number;
  amount_off: number;
  course_id: string | null;
  bundle_id: string | null;
  active: boolean;
  max_uses: number | null;
  uses: number;
}

export async function getPublishedBundles(): Promise<Bundle[]> {
  try {
    const { data } = await supabase.from('bundles').select('*').eq('is_published', true).order('price_value');
    return ((data ?? []) as unknown as Bundle[]);
  } catch {
    return [];
  }
}

export function discountFor(promo: Promo, baseAmount: number): number {
  let off = 0;
  if (promo.percent_off > 0) off = Math.round((baseAmount * promo.percent_off) / 100);
  else off = promo.amount_off;
  return Math.max(Math.min(off, baseAmount), 0);
}

export async function validatePromo(code: string, courseId?: string, bundleId?: string): Promise<{ promo: Promo; discount: number } | { error: string }> {
  const c = code.trim().toUpperCase();
  if (!c) return { error: 'Enter a code.' };
  const { data, error } = await supabase.from('promo_codes').select('*').eq('code', c).maybeSingle();
  if (error || !data) return { error: 'Code not found.' };
  const promo = data as unknown as Promo;
  if (!promo.active) return { error: 'Code is no longer active.' };
  if (promo.max_uses != null && promo.uses >= promo.max_uses) return { error: 'Code has reached its usage limit.' };
  if (promo.course_id && promo.course_id !== courseId) return { error: 'Code is not valid for this course.' };
  if (promo.bundle_id && promo.bundle_id !== bundleId) return { error: 'Code is not valid for this bundle.' };
  return { promo, discount: -1 }; // discount computed against base at checkout
}

export async function enrollInBundle(userId: string, bundle: Bundle, paystackRef?: string) {
  for (const courseId of bundle.course_ids) {
    try {
      await enrollInCourse(userId, courseId, paystackRef ? `${paystackRef}:${bundle.id}` : undefined);
    } catch {
      /* continue with remaining courses */
    }
  }
  await confirmPaymentClientSide({ ref: paystackRef || `LSEM-${bundle.id}-${Date.now()}`, userId, courseId: bundle.course_ids[0] || bundle.id, amount: bundle.price_value });
}

// ---- admin ----
export async function listBundlesAdmin(): Promise<Bundle[]> {
  const { data } = await supabase.from('bundles').select('*').order('price_value');
  return ((data ?? []) as unknown as Bundle[]);
}

export async function saveBundleAdmin(b: Partial<Bundle> & { id: string }) {
  const { error } = await supabase.from('bundles').upsert(
    {
      id: b.id,
      slug: b.slug || b.id,
      title: b.title || b.id,
      description: b.description || '',
      course_ids: b.course_ids || [],
      price_value: b.price_value ?? 0,
      compare_at_price: b.compare_at_price ?? 0,
      is_published: b.is_published ?? false,
    },
    { onConflict: 'id' }
  );
  if (error) throw new Error(error.message);
}

export async function deleteBundleAdmin(id: string) {
  const { error } = await supabase.from('bundles').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

export async function listPromosAdmin(): Promise<Promo[]> {
  const { data } = await supabase.from('promo_codes').select('*').order('code');
  return ((data ?? []) as unknown as Promo[]);
}

export async function savePromoAdmin(p: { code: string; percent_off?: number; amount_off?: number; course_id?: string | null; active?: boolean }) {
  const { error } = await supabase.from('promo_codes').upsert(
    {
      code: p.code.trim().toUpperCase(),
      percent_off: p.percent_off ?? 0,
      amount_off: p.amount_off ?? 0,
      course_id: p.course_id || null,
      active: p.active ?? true,
    },
    { onConflict: 'code' }
  );
  if (error) throw new Error(error.message);
}

export async function deletePromoAdmin(code: string) {
  const { error } = await supabase.from('promo_codes').delete().eq('code', code);
  if (error) throw new Error(error.message);
}
